import { io, Socket } from 'socket.io-client';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('features-voiceagent');

// Debug flags
const WS_DEBUG = true; // Set to true to see WebSocket event logs
const WS_EVENT_DEBUG = true; // Set to true to see all incoming events

const DEFAULT_BACKEND_URL = 'wss://d1i0o9m17iq6ao.cloudfront.net'; // Default production URL
const LOCAL_BACKEND_URL = 'ws://localhost:3000'; // Local development URL

export type WebSocketConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

class WebSocketService {
  private socket: Socket | null = null;
  private connectionStatus: WebSocketConnectionStatus = 'disconnected';
  private onStatusChangeCallback: ((status: WebSocketConnectionStatus) => void) | null = null;
  private currentUrl: string = DEFAULT_BACKEND_URL;

  public initialize(url?: string): void {
    if (this.socket) {
      logger.warn('WebSocketService already initialized.');
      return;
    }

    // Use provided URL or determine based on environment
    this.currentUrl = url || (window.location.hostname === 'localhost' ? LOCAL_BACKEND_URL : DEFAULT_BACKEND_URL);
    
    logger.info(`Initializing WebSocket connection to ${this.currentUrl}`);
    this.updateStatus('connecting');

    // Parse URL to extract base URL and query parameters for Socket.IO
    let baseUrl = this.currentUrl;
    let queryParams: { [key: string]: string } = {};
    
    if (this.currentUrl.includes('?')) {
      const [base, queryString] = this.currentUrl.split('?');
      baseUrl = base;
      
      // Parse query parameters
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        queryParams[key] = value;
      });
    }

    const socketPath = '/voice-agent/socket.io/';
    
    // Log the complete connection details
    logger.info(`🔌 SOCKET_CONNECTION: Full connection details:`);
    logger.info(`🔌 SOCKET_CONNECTION: Original URL: ${this.currentUrl}`);
    logger.info(`🔌 SOCKET_CONNECTION: Base URL: ${baseUrl}`);
    logger.info(`🔌 SOCKET_CONNECTION: Socket.IO path: ${socketPath}`);
    logger.info(`🔌 SOCKET_CONNECTION: Query params:`, queryParams);
    logger.info(`🔌 SOCKET_CONNECTION: Final WebSocket URL will be: ${baseUrl}${socketPath}?${new URLSearchParams(queryParams).toString()}`);

    // Options from main.js: transports, path, reconnectionAttempts, reconnectionDelay
    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      path: socketPath, // Standard Socket.IO path (Through the ALB to EC2)
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: queryParams, // Pass query parameters here
    });

    logger.info(`Socket.IO connecting to: ${baseUrl} with path: ${socketPath} and query:`, queryParams);

    // Add general event listener to debug all incoming events
    this.socket.onAny((eventName, ...args) => {
      if (WS_EVENT_DEBUG) {
        logger.info(`🔌 WS_EVENT: Received event: ${eventName}`, args);
      }
    });

    this.socket.on('connect', () => {
      this.updateStatus('connected');
      logger.info('🔌 SOCKET_SUCCESS: WebSocket connected successfully!');
      logger.info('🔌 SOCKET_SUCCESS: Socket ID:', this.socket?.id);
      logger.info('🔌 SOCKET_SUCCESS: Socket connected:', this.socket?.connected);
      logger.info('🔌 SOCKET_SUCCESS: Socket disconnected:', this.socket?.disconnected);
    });

    this.socket.on('disconnect', (reason) => {
      this.updateStatus('disconnected');
      logger.info(`🔌 SOCKET_DISCONNECT: WebSocket disconnected: ${reason}`);
      // Handle different reasons for disconnection if needed
    });

    this.socket.on('connect_error', (error) => {
      this.updateStatus('error');
      logger.error('🔌 SOCKET_ERROR: WebSocket connection error:', error);
      logger.error('🔌 SOCKET_ERROR: Error type:', typeof error);
      logger.error('🔌 SOCKET_ERROR: Error message:', error?.message);
      logger.error('🔌 SOCKET_ERROR: Error description:', (error as any)?.description);
      logger.error('🔌 SOCKET_ERROR: Error context:', (error as any)?.context);
      logger.error('🔌 SOCKET_ERROR: Error type property:', (error as any)?.type);
    });

    // Generic error handler
    this.socket.on('error', (error) => {
      // This might overlap with connect_error or be a general operational error
      logger.error('🔌 SOCKET_ERROR: WebSocket generic error:', error);
      logger.error('🔌 SOCKET_ERROR: Generic error type:', typeof error);
      logger.error('🔌 SOCKET_ERROR: Generic error message:', error?.message);
      // Consider if status should always be 'error' here or if it depends on the error type
      if (this.connectionStatus !== 'connected') {
        this.updateStatus('error');
      }
    });
  }

  // Create socket without auto-connecting
  public createSocketOnly(url?: string): void {
    if (this.socket) {
      logger.warn('WebSocketService socket already exists.');
      return;
    }

    // Use provided URL or determine based on environment
    this.currentUrl = url || (window.location.hostname === 'localhost' ? LOCAL_BACKEND_URL : DEFAULT_BACKEND_URL);
    
    logger.info(`Creating WebSocket instance for ${this.currentUrl} (without connecting)`);

    // Parse URL to extract base URL and query parameters for Socket.IO
    let baseUrl = this.currentUrl;
    let queryParams: { [key: string]: string } = {};
    
    if (this.currentUrl.includes('?')) {
      const [base, queryString] = this.currentUrl.split('?');
      baseUrl = base;
      
      // Parse query parameters
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        queryParams[key] = value;
      });
    }

    const socketPath = '/voice-agent/socket.io/';
    
    // Log the complete connection details for createSocketOnly
    logger.info(`🔌 SOCKET_CREATE_ONLY: Full connection details:`);
    logger.info(`🔌 SOCKET_CREATE_ONLY: Original URL: ${this.currentUrl}`);
    logger.info(`🔌 SOCKET_CREATE_ONLY: Base URL: ${baseUrl}`);
    logger.info(`🔌 SOCKET_CREATE_ONLY: Socket.IO path: ${socketPath}`);
    logger.info(`🔌 SOCKET_CREATE_ONLY: Query params:`, queryParams);
    logger.info(`🔌 SOCKET_CREATE_ONLY: Final WebSocket URL will be: ${baseUrl}${socketPath}?${new URLSearchParams(queryParams).toString()}`);

    // Create socket with autoConnect: false to prevent immediate connection
    this.socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      path: socketPath, // Standard Socket.IO path (Through the ALB to EC2)
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      query: queryParams,
      autoConnect: false, // This prevents auto-connection
    });

    logger.info(`Socket.IO instance created for: ${baseUrl} with path: ${socketPath} and query:`, queryParams);

    // Add general event listener to debug all incoming events
    this.socket.onAny((eventName, ...args) => {
      if (WS_EVENT_DEBUG) {
        logger.info(`🔌 WS_EVENT: Received event: ${eventName}`, args);
      }
    });

    this.socket.on('connect', () => {
      this.updateStatus('connected');
      logger.info('🔌 SOCKET_SUCCESS: WebSocket connected successfully! (createSocketOnly)');
      logger.info('🔌 SOCKET_SUCCESS: Socket ID:', this.socket?.id);
      logger.info('🔌 SOCKET_SUCCESS: Socket connected:', this.socket?.connected);
      logger.info('🔌 SOCKET_SUCCESS: Socket disconnected:', this.socket?.disconnected);
    });

    this.socket.on('disconnect', (reason) => {
      this.updateStatus('disconnected');
      logger.info(`🔌 SOCKET_DISCONNECT: WebSocket disconnected: ${reason} (createSocketOnly)`);
    });

    this.socket.on('connect_error', (error) => {
      this.updateStatus('error');
      logger.error('🔌 SOCKET_ERROR: WebSocket connection error (createSocketOnly):', error);
      logger.error('🔌 SOCKET_ERROR: Error type:', typeof error);
      logger.error('🔌 SOCKET_ERROR: Error message:', error?.message);
      logger.error('🔌 SOCKET_ERROR: Error description:', (error as any)?.description);
      logger.error('🔌 SOCKET_ERROR: Error context:', (error as any)?.context);
      logger.error('🔌 SOCKET_ERROR: Error type property:', (error as any)?.type);
    });

    this.socket.on('error', (error) => {
      logger.error('🔌 SOCKET_ERROR: WebSocket generic error (createSocketOnly):', error);
      logger.error('🔌 SOCKET_ERROR: Generic error type:', typeof error);
      logger.error('🔌 SOCKET_ERROR: Generic error message:', error?.message);
      if (this.connectionStatus !== 'connected') {
        this.updateStatus('error');
      }
    });
  }

  private updateStatus(status: WebSocketConnectionStatus): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      if (this.onStatusChangeCallback) {
        this.onStatusChangeCallback(status);
      }
    }
  }

  public connect(url?: string): void {
    logger.info(`🔌 WebSocketService.connect() called with URL: "${url}"`);
    logger.info(`🔌 Current URL: "${this.currentUrl}"`);
    logger.info(`🔌 Socket exists: ${!!this.socket}`);
    logger.info(`🔌 Socket disconnected: ${this.socket?.disconnected}`);
    
    // If URL is provided and different from current, reinitialize
    if (url && url !== this.currentUrl) {
      logger.info(`🔌 Switching connection from ${this.currentUrl} to ${url}`);
      this.disconnect();
      this.socket = null; // Reset socket to force reinitialization
      this.initialize(url);
      return;
    }

    if (!this.socket) {
      logger.info(`🔌 No socket exists, initializing with URL: ${url}`);
      this.initialize(url); // Initialize if not already done (e.g. explicit disconnect then connect)
      return;
    }
    
    // If socket exists but is disconnected, connect it
    if (this.socket.disconnected) {
      logger.info(`🔌 Socket exists but is disconnected, attempting to connect`);
      this.updateStatus('connecting');
      this.socket.connect();
    } else if (this.socket.connected) {
      logger.info(`🔌 Socket already connected, status: ${this.connectionStatus}`);
    } else {
      // Socket exists but not connected yet (was created with autoConnect: false)
      logger.info(`🔌 Socket exists but not connected, starting connection`);
      this.updateStatus('connecting');
      this.socket.connect();
    }
  }

  public disconnect(): void {
    const stack = new Error().stack; // Get stack trace
    logger.info(`WebSocketService: disconnect() called. Current status: ${this.connectionStatus}. Socket connected: ${this.socket?.connected}. Stack:`, stack);
    if (this.socket?.connected) {
      logger.info('WebSocketService: Disconnecting WebSocket actively.');
      this.socket.disconnect();
      // Status will be updated by the 'disconnect' event handler to 'disconnected'
    } else if (this.socket) {
      logger.warn(`WebSocketService: disconnect() called but socket not connected. Status: ${this.connectionStatus}, socket.connected: ${this.socket.connected}`);
    } else {
      logger.warn("WebSocketService: disconnect() called but socket is null.");
    }
  }

  public emitEvent(eventName: string, data: any): void {
    if (this.socket?.connected) {
      if (WS_DEBUG) {
        logger.info(`🔌 WS_EMIT: Emitting event '${eventName}'`, data ? "with data" : "without data");
      }
      this.socket.emit(eventName, data);
    } else {
      logger.warn(`🔌 WS_EMIT: Cannot emit event '${eventName}'. Socket not connected. Current status: ${this.connectionStatus}`);
    }
  }

  public onEvent(eventName: string, handler: (...args: any[]) => void): void {
    if (this.socket) {
      if (WS_DEBUG) {
        logger.info(`🔌 WS_HANDLER: Registering handler for event '${eventName}'`);
      }
      this.socket.on(eventName, handler);
    } else {
      logger.warn(`🔌 WS_HANDLER: Cannot register handler for '${eventName}'. Socket not initialized.`);
    }
  }
  
  public offEvent(eventName: string, handler?: (...args: any[]) => void): void {
    if (this.socket) {
      this.socket.off(eventName, handler);
    } else {
      logger.warn(`Cannot unregister handler for '${eventName}'. Socket not initialized.`);
    }
  }

  public getConnectionStatus(): WebSocketConnectionStatus {
    return this.connectionStatus;
  }

  public isInitialized(): boolean {
    return this.socket !== null;
  }

  public onConnectionStatusChange(callback: (status: WebSocketConnectionStatus) => void): void {
    this.onStatusChangeCallback = callback;
  }
}

// Export a singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
